import { Select } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getFullArchiveWarningText } from 'horizon/routes/WorkOrders/ArchiveTaskButton/helpers';
import { EWorkContainerArchiveOptions } from 'horizon/routes/WorkOrders/Tasks/TaskList/types';
import { useGetArchiveDescendantsCountLazyQuery } from 'types/atlas-graphql';

import { StyledAlert } from './WorkContainerArchivedChooser.style';

interface IWorkContainerArchivedChooserProps {
  idsToUpdate: string[];
  onChange?: any;
  disabled?: boolean;
  // Keep below to continue working with our js files
  props?: any;
}

export const WorkContainerArchivedChooser: React.FunctionComponent<
  IWorkContainerArchivedChooserProps
> = ({ idsToUpdate, onChange, disabled, props }) => {
  const { t } = useTranslation();
  const [archivedValue, setArchivedValue] = useState<string>('');
  const [archiveDescendantsCount, setArchiveDescendentsCount] = useState<any>({});
  const [warningMessage, setWarningMessage] = useState<string>('');

  const [getArchiveCount] = useGetArchiveDescendantsCountLazyQuery({
    // This is needed to avoid reusing stale results after we bulk archive work containers
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    // check if the value is defined before calling the query, to prevent returning misleading data
    if (archivedValue) {
      getArchiveCount({
        variables: {
          input: {
            ids: idsToUpdate,
            archiveValue: archivedValue === EWorkContainerArchiveOptions.archived,
          },
        },
      }).then(result => {
        if (result.data) {
          setArchiveDescendentsCount(result.data.archiveDescendantsCount);
        }
      });
    }
  }, [archivedValue, getArchiveCount, idsToUpdate]);

  useEffect(() => {
    if (archiveDescendantsCount) {
      const warningText = getFullArchiveWarningText(archiveDescendantsCount);
      if (!archivedValue || warningText === undefined) {
        return;
      }
      if (warningText === '') {
        setWarningMessage(warningText);
      } else {
        const fullMessage =
          archivedValue === EWorkContainerArchiveOptions.archived
            ? t('workOrder.archiveContainersWarning', 'This will archive {{items}}', {
                items: warningText,
              })
            : t('workOrder.unarchiveContainersWarning', 'This will unarchive {{items}}', {
                items: warningText,
              });
        setWarningMessage(fullMessage);
      }
    }
  }, [archiveDescendantsCount, archivedValue, t]);

  const handleOnChange = (selectedValue: string) => {
    setArchivedValue(selectedValue);
    onChange(selectedValue);
  };

  return (
    <>
      <Select
        defaultValue={archivedValue}
        onChange={handleOnChange}
        disabled={disabled}
        {...props}
        options={[
          {
            value: EWorkContainerArchiveOptions.archived,
            label: t('workOrder.status.archived', 'Archived'),
          },
          {
            value: EWorkContainerArchiveOptions.unarchived,
            label: t('workOrder.unarchived', 'Unarchived'),
          },
        ]}
      />
      {warningMessage !== '' && !disabled ? (
        <StyledAlert message={warningMessage} type="warning" showIcon />
      ) : (
        ''
      )}
    </>
  );
};
